import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import { VersionChanges, ChangedItem } from '@/utils/changeOrderExportUtils';
import { getFinalLineItemUnitCost, getFinalLineItemAmount } from '@/utils/estimateExportUtils';
import { getDisplayDescription } from '@/utils/lineItemDisplayHelpers';
import { Badge } from '@/components/ui/badge';
import { Plus, Minus, Edit } from 'lucide-react';

interface ComprehensiveChangeOrderTableProps {
  changes: VersionChanges;
  baseVersionNumber: number;
  changeOrderNumber: number;
  getUnitSymbol: (unit: string) => string;
  taxAmount?: number;
  totalAmount?: number;
  isVatRegistered?: boolean;
  companyVatRate?: number;
  allLineItems?: any[]; // All line items from the current version
}

const ComprehensiveChangeOrderTable: React.FC<ComprehensiveChangeOrderTableProps> = ({
  changes,
  baseVersionNumber,
  changeOrderNumber,
  getUnitSymbol,
  taxAmount = 0,
  totalAmount = 0,
  isVatRegistered = false,
  companyVatRate = 15,
  allLineItems = []
}) => {
  const showVat = isVatRegistered && companyVatRate > 0;

  const getChangeIcon = (changeType: string) => {
    switch (changeType) {
      case 'added':
        return <Plus className="h-3 w-3 text-green-600" />;
      case 'removed':
        return <Minus className="h-3 w-3 text-red-600" />;
      case 'modified':
        return <Edit className="h-3 w-3 text-blue-600" />;
      default:
        return null;
    }
  };

  const getChangeColor = (changeType: string) => {
    switch (changeType) {
      case 'added':
        return 'bg-green-50 border-green-200';
      case 'removed':
        return 'bg-red-50 border-red-200';
      case 'modified':
        return 'bg-blue-50 border-blue-200';
      default:
        return '';
    }
  };

  // Create a map of changed items for quick lookup
  const changedItemMap = new Map();
  
  [...changes.added, ...changes.removed, ...changes.modified].forEach(item => {
    const key = `${item.component_type}-${item.component_id}`;
    changedItemMap.set(key, item);
  });

  const renderItemWithChangeIndicator = (item: any, changeType?: string) => {
    // Get the changed item data for this specific item
    const changedItem = changedItemMap.get(`${item.component_type}-${item.component_id}`);
    
    // Helper function to get quantity handling both formats
    const getItemQuantity = (item: any) => {
      return item.quantity !== undefined ? item.quantity : (item.quantity_in_base_units || 0);
    };

    const unitCost = changeType === 'modified' ? 
      (changedItem?.newUnitCost || 0) :
      changeType === 'removed' ? 0 : 
      getFinalLineItemUnitCost(item);
    
    const quantity = changeType === 'modified' ? 
      (changedItem?.newQuantity || 0) :
      changeType === 'removed' ? 0 :
      getItemQuantity(item);
    
    const amount = changeType === 'modified' ? 
      ((changedItem?.newTotal || 0) - (changedItem?.oldTotal || 0)) :
      changeType === 'removed' ? -getFinalLineItemAmount(item) :
      getFinalLineItemAmount(item);

    return (
      <TableRow key={`${item.component_type}-${item.component_id}`} className={changeType ? getChangeColor(changeType) : ''}>
        <TableCell className="text-left border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', wordWrap: 'break-word', width: '60% !important', padding: '8px 16px' }}>
          <div className="flex items-center gap-2">
            {changeType && getChangeIcon(changeType)}
            <div>
              {changeType === 'added' && (
                <span><strong>NEW:</strong> {getDisplayDescription(item)}</span>
              )}
              {changeType === 'removed' && (
                <span><strong>REMOVED:</strong> {getDisplayDescription(item)}</span>
              )}
              {changeType === 'modified' && (
                <div>
                  <div className="font-medium">{getDisplayDescription(item)}</div>
                  {changedItem && (
                    <div className="text-xs mt-1 space-y-0.5">
                      {changedItem.oldQuantity !== undefined && changedItem.newQuantity !== undefined && changedItem.oldQuantity !== changedItem.newQuantity && (
                        <div>
                          <span className="text-muted-foreground">Quantity: </span>
                          <span className="text-red-600">{changedItem.oldQuantity}</span>
                          <span className="text-muted-foreground"> → </span>
                          <span className="text-green-600">{changedItem.newQuantity}</span>
                        </div>
                      )}
                      {changedItem.oldUnitCost !== undefined && changedItem.newUnitCost !== undefined && Math.abs(changedItem.oldUnitCost - changedItem.newUnitCost) > 0.01 && (
                        <div>
                          <span className="text-muted-foreground">Unit Cost: </span>
                          <span className="text-red-600">{formatCurrency(changedItem.oldUnitCost)}</span>
                          <span className="text-muted-foreground"> → </span>
                          <span className="text-green-600">{formatCurrency(changedItem.newUnitCost)}</span>
                        </div>
                      )}
                      {changedItem.oldTotal !== undefined && changedItem.newTotal !== undefined && (
                        <div>
                          <span className="text-muted-foreground">Total: </span>
                          <span className="text-red-600">{formatCurrency(changedItem.oldTotal)}</span>
                          <span className="text-muted-foreground"> → </span>
                          <span className="text-green-600">{formatCurrency(changedItem.newTotal)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {!changeType && getDisplayDescription(item)}
            </div>
          </div>
        </TableCell>
        <TableCell className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '18% !important', padding: '8px 16px' }}>
          {changeType === 'removed' ? '-' : formatCurrency(Math.abs(unitCost))}
        </TableCell>
        <TableCell className="text-center border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '7% !important', padding: '4px !important', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '7% !important' }}>
          {changeType === 'removed' ? '-' : getUnitSymbol(item.base_unit)}
        </TableCell>
        <TableCell className="text-center border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15', width: '9% !important', padding: '4px !important', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '9% !important' }}>
          {changeType === 'removed' ? '-' : quantity}
        </TableCell>
        <TableCell className={`text-right font-medium whitespace-nowrap border-table-cell ${changeType === 'modified' && amount > 0 ? 'text-green-600' : changeType === 'modified' && amount < 0 ? 'text-red-600' : ''}`} style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: '500', width: '14% !important', padding: '8px 16px' }}>
          {changeType === 'modified' ? (amount > 0 ? '+' : '') + formatCurrency(amount) : 
           changeType === 'removed' ? '-' + formatCurrency(Math.abs(amount)) :
           formatCurrency(amount)}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="mb-8">
      {/* Change Order Header */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded change-order-header">
        <h3 className="text-lg font-semibold text-blue-900 mb-2 change-order-title">
          Change Order v{changeOrderNumber}
        </h3>
        <p className="text-sm text-blue-700 change-order-subtitle">
          Changes from approved version v{baseVersionNumber}:
        </p>
        <div className="flex gap-4 mt-2 text-xs change-summary">
          <span className="flex items-center gap-1 text-green-600 change-added">
            <Plus className="h-3 w-3" />
            {changes.added.length} Added
          </span>
          <span className="flex items-center gap-1 text-red-600 change-removed">
            <Minus className="h-3 w-3" />
            {changes.removed.length} Removed
          </span>
          <span className="flex items-center gap-1 text-blue-600 change-modified">
            <Edit className="h-3 w-3" />
            {changes.modified.length} Modified
          </span>
        </div>
      </div>

      {/* Previous Total */}
      <div className="mb-4">
        <Table className="w-full estimate-table" style={{ tableLayout: 'fixed' }}>
          <TableBody>
            <TableRow>
              <TableCell 
                colSpan={4} 
                className="text-left font-semibold border-table-cell bg-gray-100" 
                style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}
              >
                Previous Total (Version v{baseVersionNumber})
              </TableCell>
              <TableCell className="text-right font-semibold whitespace-nowrap border-table-cell bg-gray-100" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}>
                {formatCurrency(changes.baseTotal)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* Complete Line Items Table with Change Indicators */}
      <Table className="mb-4 w-full estimate-table" style={{ tableLayout: 'fixed' }}>
        <TableHeader>
          <TableRow>
            <TableHead className="text-left border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '60% !important', whiteSpace: 'nowrap', padding: '8px 16px' }}>
              Complete Line Items (Including Changes)
            </TableHead>
            <TableHead className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '18% !important', padding: '8px 16px' }}>Unit Price</TableHead>
            <TableHead className="text-center whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '7% !important', padding: '4px !important', maxWidth: '7% !important' }}>Unit</TableHead>
            <TableHead className="text-center whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '9% !important', padding: '4px !important', maxWidth: '9% !important' }}>Quantity</TableHead>
            <TableHead className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', fontWeight: 'bold', lineHeight: '1.15', width: '14% !important', padding: '8px 16px' }}>Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {allLineItems.map((item) => {
            const key = `${item.component_type}-${item.component_id}`;
            const changedItem = changedItemMap.get(key);
            
            if (changedItem) {
              // Item has changes - render with change indicator
              return renderItemWithChangeIndicator(item, changedItem.changeType);
            } else {
              // Unchanged item - render normally
              return renderItemWithChangeIndicator(item);
            }
          })}
          
          {/* Add removed items that are not in current line items */}
          {changes.removed.map((item) => renderItemWithChangeIndicator(item, 'removed'))}
          
          {/* Change Subtotal */}
          <TableRow>
            <TableCell 
              colSpan={4} 
              className="text-left font-semibold border-table-cell" 
              style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}
            >
              Change Amount
            </TableCell>
            <TableCell className={`text-right font-semibold whitespace-nowrap border-table-cell ${changes.totalDifference > 0 ? 'text-green-600' : changes.totalDifference < 0 ? 'text-red-600' : ''}`} style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}>
              {changes.totalDifference > 0 ? '+' : ''}{formatCurrency(changes.totalDifference)}
            </TableCell>
          </TableRow>

          {/* VAT Row if applicable */}
          {showVat && (
            <TableRow>
              <TableCell 
                colSpan={4} 
                className="text-left border-table-cell" 
                style={{ fontSize: '10px', lineHeight: '1.15' }}
              >
                VAT ({companyVatRate}%)
              </TableCell>
              <TableCell className="text-right whitespace-nowrap border-table-cell" style={{ fontSize: '10px', lineHeight: '1.15' }}>
                {formatCurrency(taxAmount)}
              </TableCell>
            </TableRow>
          )}

          {/* New Total */}
          <TableRow>
            <TableCell 
              colSpan={4} 
              className="text-left font-bold border-table-cell bg-blue-50" 
              style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}
            >
              New Total
            </TableCell>
            <TableCell className="text-right font-bold whitespace-nowrap border-table-cell bg-blue-50" style={{ fontSize: '10px', lineHeight: '1.15', fontWeight: 'bold' }}>
              {formatCurrency(totalAmount)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};

export default ComprehensiveChangeOrderTable;
